import { LEADERBOARD_POINTS_CONFIG } from "@/src/constants/leaderboard.constant";
import { useAuth } from "@/src/contexts/auth.context";
import { useReactToPost } from "@/src/hooks/features/posts/use-react-to-post";
import { useShareWithPoints } from "@/src/hooks/features/posts/use-share-with-points";
import { LeaderboardEntry } from "@/src/models/leaderboard.model";
import { Post } from "@/src/models/post.model";
import {
  LEADERBOARD_QUERY_KEYS,
  useUpsertLeaderboardPoints,
} from "@/src/services/api/leaderboard/leaderboard.hooks";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import {
  useCreateCommentMutation,
  useGetCommentsQuery,
  useGetPostByIdQuery,
} from "@/src/services/api/posts/posts.hooks";
import { splitAttachmentUrls } from "@/src/utils/attachment.utils";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "react-native";
import { useToggleSavePost } from "./use-toggle-save-post";

type UsePostDetailOptions = Readonly<{
  postId: string;
  visible: boolean;
}>;

export const usePostDetail = ({ postId, visible }: UsePostDetailOptions) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userId = user?.itemId ?? "";

  const queryClient = useQueryClient();
  const upsertPoints = useUpsertLeaderboardPoints();
  const { handleSharedSuccessfully } = useShareWithPoints();

  const [commentText, setCommentText] = useState("");
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [replyTarget, setReplyTarget] = useState<{
    commentId: string;
    author: string;
  } | null>(null);

  const inputRef = useRef<TextInput>(null);
  const { data: activePost, isLoading: isPostLoading } = useGetPostByIdQuery(
    postId,
    { enabled: visible && !!postId },
  );
  const { isSaved, handleToggleSave } = useToggleSavePost(activePost ?? null);

  const { userReaction, handleReact } = useReactToPost(
    activePost ?? ({} as Post),
  );

  const { mediaUrls, fileUrls } = splitAttachmentUrls(
    activePost?.attachmentUrls,
  );

  const {
    data: commentsData,
    isLoading: isCommentsLoading,
    isError: isCommentsError,
    refetch: refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetCommentsQuery(activePost?.ItemId ?? "", undefined);

  const comments =
    commentsData?.pages.flatMap((page) => page.getcomments.items) ?? [];

  const { mutate: createComment, isPending: isCommentPending } =
    useCreateCommentMutation();

  const { mutate: sendNotification } = useSendNotification();

  const isCommentDisabled = !commentText.trim();

  const awardCommentPoints = () => {
    if (!userId) return;
    const delta = LEADERBOARD_POINTS_CONFIG["comment"];
    if (!delta) return;

    const currentEntry =
      queryClient.getQueryData<LeaderboardEntry | null>(
        LEADERBOARD_QUERY_KEYS.byUser(userId),
      ) ?? null;

    upsertPoints.mutate({ userId, pointsDelta: delta, currentEntry });
  };

  const handlePostComment = () => {
    if (!commentText.trim() || !activePost) return;

    const parentComment = replyTarget
      ? comments.find((comment) => comment.ItemId === replyTarget.commentId)
      : null;

    createComment(
      {
        postId: activePost.ItemId,
        content: commentText.trim(),
        parentCommentId: replyTarget?.commentId,
        parentReplyCount: parentComment?.replyCount,
        postCommentCount: activePost.commentCount,
        authorId: user?.itemId ?? "",
        authorSnapshot: {
          firstName: user?.firstName,
          lastName: user?.lastName,
          profileImageUrl: user?.profileImageUrl ?? "",
        },
        replyCount: 0,
      },
      {
        onSuccess: () => {
          setCommentText("");
          setReplyTarget(null);
          awardCommentPoints();

          if (activePost.authorSnapshot?.userId !== user?.itemId) {
            sendNotification({
              userIds: [activePost.authorSnapshot.userId],
              denormalizedPayload: JSON.stringify({
                type: "post_comment",
                message: t("LABEL_NOTIFY_POST_COMMENT", {
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  preview: `${commentText.trim().slice(0, 80)}${commentText.trim().length > 80 ? "…" : ""}`,
                }),
                actorAvatar: user?.profileImageUrl,
                targetEntityId: activePost.ItemId,
              }),
              configuratoinName: "post-comment",
              responseKey: "post-action",
              responseValue: "comment",
            });
          }
        },
      },
    );
  };

  const handleReply = (commentId: string, author: string) => {
    setReplyTarget({ commentId, author });
    inputRef.current?.focus();
  };

  const handleCancelReply = () => setReplyTarget(null);

  const handleShareNotification = () => {
    if (!activePost || activePost.authorSnapshot?.userId === user?.itemId)
      return;
    sendNotification({
      userIds: [activePost.authorSnapshot.userId],
      denormalizedPayload: JSON.stringify({
        type: "post_share",
        message: t("LABEL_NOTIFY_POST_SHARE", {
          firstName: user?.firstName,
          lastName: user?.lastName,
        }),
        actorAvatar: user?.profileImageUrl,
        targetEntityId: activePost.ItemId,
      }),
      configuratoinName: "post-share",
      responseKey: "post-action",
      responseValue: "share",
    });
  };
  const handleShareSuccess = () => {
    setShareModalVisible(false);
    handleSharedSuccessfully();
  };

  return {
    user,
    // post
    activePost,
    isPostLoading,
    mediaUrls,
    fileUrls,
    // reactions
    userReaction,
    handleReact,
    // comments
    comments,
    isCommentsLoading,
    isCommentsError,
    refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    // comment input
    commentText,
    setCommentText,
    isCommentDisabled,
    isCommentPending,
    handlePostComment,
    inputRef,
    // reply
    replyTarget,
    handleReply,
    handleCancelReply,
    // share
    shareModalVisible,
    setShareModalVisible,
    handleShareNotification,
    handleShareSuccess,

    // Save
    isSaved,
    handleToggleSave,
  };
};
